import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, filter, interval, map, switchMap, tap, withLatestFrom } from 'rxjs';

import { RegistrationType } from 'app/features/manual-input-registration/models';
import { UserSessionStorageService } from 'app/features/settings/services';
import {
    selectAppModeIfDefined,
    selectAppSettingsIfDefined,
} from 'app/features/settings/state/settings/settings.selectors';
import { UserSession } from 'app/shared/models';
import { addTimeRegistration } from 'app/state/registrations/registrations.actions';
import { selectUserSession, selectUserSessionHistory } from 'app/state/user-session/user-session.selectors';
import {
    getUserSessionExpirationTime,
    isUserSessionExpired,
} from 'app/state/user-session/user-session.utils';

import {
    initializeUserSessionStore,
    initializeUserSessionStoreSuccess,
    loginSuccess,
    startUserSessionExpirationMonitoring,
    supervisorLogout,
    userLogout,
    userSessionClosed,
    userSessionExpiring,
    userSessionHistoryUpdated,
} from './user-session.actions';

// noinspection JSUnusedGlobalSymbols
@Injectable()
export class UserSessionEffects {
    initializeAuthStore$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(initializeUserSessionStore),
            withLatestFrom(this.store.select(selectUserSession)),
            filter(([_, userSession]) => !userSession),
            map(() => {
                const userSession = this.userSessionStorageService.getUserSessionFromStorage();
                const history = this.userSessionStorageService.getUserSessionHistory();
                return initializeUserSessionStoreSuccess({ userSession, history });
            }),
        );
    });

    startUserSessionExpirationMonitoringTrigger$ = createEffect(() =>
        this.actions$.pipe(
            ofType(loginSuccess, initializeUserSessionStoreSuccess),
            map(() => startUserSessionExpirationMonitoring()),
        ),
    );

    sendLoginTimeRegistration$ = createEffect(() =>
        this.actions$.pipe(
            ofType(loginSuccess),
            withLatestFrom(this.store.pipe(selectAppModeIfDefined)),
            filter(([, appMode]) => appMode === 'personal'),
            map(([{ userSession }]) =>
                addTimeRegistration({
                    activityNumber: undefined,
                    employeeNumber: userSession.credentials.employeeNumber,
                    registrationTimestamp: Date.now(),
                    registrationType: RegistrationType.Login,
                }),
            ),
        ),
    );

    sendLogoutTimeRegistrationIfUserLogsOut$ = createEffect(() =>
        this.actions$.pipe(
            ofType(userSessionClosed),
            filter(({ sessionExpired }) => !sessionExpired),
            withLatestFrom(this.store.pipe(selectAppModeIfDefined)),
            filter(([, appMode]) => appMode === 'personal'),
            map(([{ userSession, timestamp }]) =>
                addTimeRegistration({
                    activityNumber: undefined,
                    employeeNumber: userSession!.credentials.employeeNumber,
                    registrationTimestamp: timestamp,
                    registrationType: RegistrationType.Logout,
                }),
            ),
        ),
    );

    syncUserSessionInStorage$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(initializeUserSessionStoreSuccess),
                switchMap(() => this.store.select(selectUserSession)),
                tap((userSession) => {
                    if (userSession) {
                        this.userSessionStorageService.putUserSessionToStorage(userSession);
                    } else {
                        this.userSessionStorageService.clearUserSessionFromStorage();
                    }
                }),
            );
        },
        { dispatch: false },
    );

    userLogoutRollOut$ = createEffect(() =>
        this.actions$.pipe(
            ofType(userLogout, supervisorLogout),
            withLatestFrom(this.store.select(selectUserSession)),
            switchMap(([, userSession]) =>
                userSession
                    ? [
                          userSessionClosed({ userSession, timestamp: Date.now(), sessionExpired: false }),
                          userSessionHistoryUpdated({ userSession }),
                      ]
                    : EMPTY,
            ),
        ),
    );

    userSessionExpiringRollOut$ = createEffect(() =>
        this.actions$.pipe(
            ofType(userSessionExpiring),
            switchMap(({ userSession, expirationTimestamp }) => [
                userSessionClosed({ userSession, timestamp: expirationTimestamp, sessionExpired: true }),
                userSessionHistoryUpdated({ userSession: userSession }),
            ]),
        ),
    );

    putUserSessionHistoryToStorage$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(userSessionHistoryUpdated),
                withLatestFrom(this.store.select(selectUserSessionHistory)),
                tap(([, history]) => {
                    this.userSessionStorageService.putUserSessionHistoryToStorage(history);
                }),
            );
        },
        { dispatch: false },
    );

    startUserSessionExpirationMonitoring$ = createEffect(() =>
        this.actions$.pipe(
            ofType(startUserSessionExpirationMonitoring),
            switchMap(() => interval(1000)),
            withLatestFrom(this.store.select(selectUserSession)),
            map(([_, userSession]) => userSession),
            filter((userSession): userSession is UserSession => !!userSession),
            withLatestFrom(this.store.pipe(selectAppSettingsIfDefined)),
            filter(([userSession, appSettings]) => isUserSessionExpired({ userSession, appSettings })),
            map(([userSession, appSettings]) =>
                userSessionExpiring({
                    userSession,
                    expirationTimestamp: getUserSessionExpirationTime({ userSession, appSettings }),
                }),
            ),
        ),
    );

    constructor(
        private actions$: Actions,
        private store: Store,
        private userSessionStorageService: UserSessionStorageService,
    ) {}
}
